import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

import {
  SEED_TREATMENT_ADD_LABEL,
  SEED_TREATMENT_FLYOUT_CLOSE_LABEL,
} from 'constants/uiConstants';
import MergeClassNames from 'utils/MergeClassNames';

import FlyoutHeader from './FlyoutHeader';
import SeedTreatmentFields, {
  SeedTreatmentFieldsProps,
} from './SeedTreatmentFields';
import { FLYOUT_ADD_BUTTON_ATTRIBUTE } from './stepConfig';
import useFlyoutPosition from './useFlyoutPosition';

interface SeedTreatmentFlyoutProps extends SeedTreatmentFieldsProps {
  anchorRef: React.RefObject<HTMLElement>;
  title: string;
  isMultiSelectMode?: boolean;
  /** Focus the rate-mode toggle on open, so Tab continues inside the flyout (keyboard opens only). */
  autoFocusRateMode?: boolean;
  onDone: (event: React.MouseEvent) => void;
  onClose: () => void;
}

/**
 * Seed Treatment add form portaled beside the picker, like SeedVarietyFlyout.
 * Wraps SeedTreatmentFields with a title and an Add button. Add is always
 * enabled; an unlinked /100kg treatment gets the grid's "needs a seed" flag.
 */
const SeedTreatmentFlyout: React.FC<SeedTreatmentFlyoutProps> = ({
  anchorRef,
  title,
  isMultiSelectMode = false,
  autoFocusRateMode = false,
  onDone,
  onClose,
  ...fieldProps
}) => {
  const { position, portalContainer } = useFlyoutPosition(anchorRef);
  const flyoutRef = useRef<HTMLDivElement | null>(null);
  const isRendered = !!position && !!portalContainer;

  useEffect(() => {
    if (!autoFocusRateMode || !isRendered) return;
    flyoutRef.current
      ?.querySelector<HTMLInputElement>('.ant-segmented-item-input:checked')
      // focusVisible asks browsers that support it to show the ring for this script focus.
      ?.focus({ focusVisible: true } as FocusOptions);
  }, [autoFocusRateMode, isRendered]);

  if (!position || !portalContainer) return null;

  return createPortal(
    <div
      style={
        {
          '--flyout-top': `${position.top}rem`,
          '--flyout-left': `${position.left}rem`,
          '--flyout-width': `${position.flyoutWidth}rem`,
          '--flyout-height': `${position.height}rem`,
        } as React.CSSProperties
      }
      ref={flyoutRef}
      tabIndex={-1}
      className="step-picker-flyout z-[1080] flex flex-col outline-none overflow-hidden rounded-l-none rounded-lg border-l bg-white"
    >
      <FlyoutHeader
        title={title}
        closeLabel={SEED_TREATMENT_FLYOUT_CLOSE_LABEL}
        onClose={onClose}
      />
      <SeedTreatmentFields
        {...fieldProps}
        className={MergeClassNames('min-h-0 flex-1 p-3', fieldProps.className)}
      />
      <div className="p-3">
        <button
          type="button"
          {...{ [FLYOUT_ADD_BUTTON_ATTRIBUTE]: '' }}
          className={MergeClassNames(
            'w-full rounded-[var(--radius-button)] px-3 py-1.5 text-sm font-medium',
            isMultiSelectMode
              ? 'border border-gray-medium bg-white text-charcoal'
              : 'bg-success-dark text-white'
          )}
          onClick={onDone}
        >
          {SEED_TREATMENT_ADD_LABEL}
        </button>
      </div>
    </div>,
    portalContainer
  );
};

export default SeedTreatmentFlyout;
